/**
 * Error names Cognito returns when a request is refused for RATE, not for
 * what it asked — the account-wide quota of the API was exhausted at that
 * moment. Both pools word it the same way:
 *
 *   - `cognito-identity` `GetId` / `GetCredentialsForIdentity`
 *     → HTTP 400 `{"__type":"TooManyRequestsException","message":"Rate exceeded"}`
 *       (Sentry WAWIASSIST-255 / WA-3947 — the identity pool is shared with
 *       other products, so WaWi can be throttled by a neighbour's traffic);
 *   - `cognito-idp` `InitiateAuth` / `GetTokensFromRefreshToken` / `GetUser`
 *     → the same `TooManyRequestsException`.
 *
 * `ThrottlingException` is the generic AWS spelling of the same condition.
 * Amplify's own retry decider lists both as retryable and its token
 * orchestrator keeps the tokens on them (`TokenOrchestrator.isAuthenticationError`):
 * the session is intact, only this attempt was unlucky.
 *
 * Matched on `error.name` ONLY — never on the HTTP status (400 is also what a
 * misconfigured pool or an invalid token gets) or on the body size.
 */
const COGNITO_THROTTLING_ERROR_NAMES: readonly string[] = [
  'TooManyRequestsException',
  'ThrottlingException',
];

/**
 * Does this thrown value represent a Cognito rate limit? Accepts `unknown`
 * because it is used on `catch` bindings and on RxJS error values.
 */
export const isCognitoThrottlingError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const { name } = error as { name?: unknown };
  return typeof name === 'string' && COGNITO_THROTTLING_ERROR_NAMES.includes(name);
};
